import { useEffect, useRef } from 'react'
import type { LucideIcon } from 'lucide-react'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'

type Tone = 'info' | 'warning' | 'error' | 'success'
const TONE_ICON: Record<Tone, LucideIcon> = { info: Info, warning: AlertTriangle, error: XCircle, success: CheckCircle2 }

export function Notice({ tone = 'info', children }: { tone?: Tone; children: React.ReactNode }) {
  const Icon = TONE_ICON[tone]
  return (
    <div className={`notice notice-${tone}`} role={tone === 'error' ? 'alert' : undefined}>
      <Icon size={16} />
      <div>{children}</div>
    </div>
  )
}

export function Loading({ label = 'Loading...' }: { label?: string }) {
  return (
    <div className="loading" role="status">
      <div className="spinner" />
      {label}
    </div>
  )
}

export function Empty({ icon: Icon, title, children }: { icon: LucideIcon; title: string; children?: React.ReactNode }) {
  return (
    <div className="empty">
      <span className="empty-icon">
        <Icon size={20} />
      </span>
      <p className="empty-title">{title}</p>
      {children && <div className="empty-text">{children}</div>}
    </div>
  )
}

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong'
}

interface DialogProps {
  title: string
  onClose: () => void
  children: React.ReactNode
  footer?: React.ReactNode
}

/** Centred on desktop, a bottom sheet on phones. Escape and the scrim close it. */
export function Dialog({ title, onClose, children, footer }: DialogProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const previous = document.activeElement as HTMLElement | null
    ref.current?.focus()
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
      previous?.focus()
    }
  }, [onClose])

  return (
    <div
      className="scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="dialog" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={ref}>
        <div className="dialog-head">
          <h2 className="dialog-title">{title}</h2>
          <button type="button" className="btn btn-icon" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="dialog-body">{children}</div>
        {footer && <div className="dialog-foot">{footer}</div>}
      </div>
    </div>
  )
}
